import { notification } from "antd";
import cuid from "cuid";
import { filter, find, get, isEmpty, map } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import {
  CREATE_ATTACHMENT,
  DELETE_ATTACHMENT,
  GENERATE_SUBSCRIPTION_DOCUMENTS,
} from "utils/api/graphql/mutations/attachment";
import { DOWNLOAD_SINGLE_DOCUMENT } from "utils/api/graphql/mutations/download-document";
import { SUBSCRIPTION } from "utils/api/graphql/queries/subscription";
import {
  ATTACHMENT_TYPES,
  DOCUMENTS_TYPES,
  KEYS,
  PROCESS_TYPES,
  RELATIONSHIP,
  SUBSCRIPTION_STATUSES,
} from "utils/constants";
import { downloadDocument } from "utils/helpers/files";
import {
  isLastJustificationStep,
  JUSTIFICATION_STEPS,
} from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/documentsCompletion";
import useSaveDocumentsCompletion from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/useSaveDocumentsCompletion";
import { resolveIdentityDocumentSides } from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/identityDocuments";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import View from "./View";

export const DOCUMENTS_TYPE = {
  cinRecto: ATTACHMENT_TYPES.CIN_RECTO,
  cinVerso: ATTACHMENT_TYPES.CIN_VERSO,
  paymentAccount: DOCUMENTS_TYPES.PAYMENT_ACCOUNT,
  refundAccount: DOCUMENTS_TYPES.REFUND_ACCOUNT,
  socialSecurityCertificate:
    ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
  payerCinRecto: ATTACHMENT_TYPES.PAYER_CIN_RECTO,
  payerCinVerso: ATTACHMENT_TYPES.PAYER_CIN_VERSO,
  receiverCinRecto: ATTACHMENT_TYPES.RECEIVER_CIN_RECTO,
  receiverCinVerso: ATTACHMENT_TYPES.RECEIVER_CIN_VERSO,
  proofOfEligibility: DOCUMENTS_TYPES.PROOF_OF_ELIGIBILITY,
  proofOfAddress: DOCUMENTS_TYPES.PROOF_OF_ADDRESS,
  contributionPaymentAgreement: ATTACHMENT_TYPES.CONTRIBUTION_PAYMENT_AGREEMENT,
};

const Justification = ({
  currentStatus,
  tracer,
  project,
  onBack,
  updateUrl,
  onNext,
}) => {
  const [initialValues, setInitialValues] = useState(null);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const { t } = useTranslation(NAME_SPACES.PUBLIC.ACORIS);
  const TRANSLATION = t("JUSTIFICATION", { returnObjects: true });
  const { t: tErrors } = useTranslation(NAME_SPACES.ERRORS);
  const API_ERRORS = tErrors("API", { returnObjects: true });
  const subscriptionId =
    get(project, "fields.currentSubscription") ||
    localStorage.getItem(KEYS.SUBSCRIPTION_ID);

  const { data } = useQuery(SUBSCRIPTION, {
    variables: { where: { id: subscriptionId } },
  });
  const [downloadSingleDocument] = useMutation(DOWNLOAD_SINGLE_DOCUMENT);

  const [generateSubscriptionDocuments] = useMutation(
    GENERATE_SUBSCRIPTION_DOCUMENTS,
  );

  const supportingDocuments = useMemo(() => {
    const profile = get(data, "subscription.project.contact.fields.profile");
    return get(
      find(
        get(data, "subscription.insurancePlan.fields.supportingDocuments"),
        ({ profiles, processType }) =>
          processType === PROCESS_TYPES.B2C && profiles.includes(profile),
      ),
      "attachments",
      [],
    );
  }, [data]);

  const { loading: attachmentLoading, refetch: refetchAttachments } = useQuery(
    graphql.queries.ATTACHMENTS,
    {
      fetchPolicy: "no-cache",
      variables: {
        where: {
          user: {
            id: get(data, "subscription.project.contact.user.id"),
          },
          subscription: {
            id: subscriptionId,
          },
        },
        isIn: {
          type: Object.values(DOCUMENTS_TYPE),
        },
      },
      onCompleted: ({ attachments: { data: attachmentsList } }) => {
        const adherentIdentity = resolveIdentityDocumentSides(attachmentsList, {
          rectoType: ATTACHMENT_TYPES.CIN_RECTO,
          versoType: ATTACHMENT_TYPES.CIN_VERSO,
          legacyType: ATTACHMENT_TYPES.CIN,
        });
        const payerIdentity = resolveIdentityDocumentSides(attachmentsList, {
          rectoType: ATTACHMENT_TYPES.PAYER_CIN_RECTO,
          versoType: ATTACHMENT_TYPES.PAYER_CIN_VERSO,
          legacyType: ATTACHMENT_TYPES.PAYER_CIN,
        });
        const receiverIdentity = resolveIdentityDocumentSides(attachmentsList, {
          rectoType: ATTACHMENT_TYPES.RECEIVER_CIN_RECTO,
          versoType: ATTACHMENT_TYPES.RECEIVER_CIN_VERSO,
          legacyType: ATTACHMENT_TYPES.RECEIVER_CIN,
        });

        setInitialValues({
          cinRecto: adherentIdentity.recto,
          cinVerso: adherentIdentity.verso,
          payerCinRecto: payerIdentity.recto,
          payerCinVerso: payerIdentity.verso,
          receiverCinRecto: receiverIdentity.recto,
          receiverCinVerso: receiverIdentity.verso,
          paymentAccount:
            map(
              filter(
                attachmentsList,
                ({ type }) => type === ATTACHMENT_TYPES.PAYMENT_ACCOUNT,
              ),
              ({ id, name, fileUrl }) => ({
                uid: id,
                name,
                status: "done",
                url: fileUrl,
              }),
            ) || [],
          socialSecurityCertificate:
            map(
              filter(
                attachmentsList,
                ({ type }) =>
                  type === ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
              ),
              ({ id, name, fileUrl }) => ({
                uid: id,
                name,
                status: "done",
                url: fileUrl,
              }),
            ) || [],
          refundAccount:
            map(
              filter(
                attachmentsList,
                ({ type }) => type === ATTACHMENT_TYPES.REFUND_ACCOUNT,
              ),
              ({ id, name, fileUrl }) => ({
                uid: id,
                name,
                status: "done",
                url: fileUrl,
              }),
            ) || [],
          proofOfEligibility:
            map(
              filter(
                attachmentsList,
                ({ type }) => type === ATTACHMENT_TYPES.PROOF_OF_ELIGIBILITY,
              ),
              ({ id, name, fileUrl }) => ({
                uid: id,
                name,
                status: "done",
                url: fileUrl,
              }),
            ) || [],
          proofOfAddress:
            map(
              filter(
                attachmentsList,
                ({ type }) => type === ATTACHMENT_TYPES.PROOF_OF_ADDRESS,
              ),
              ({ id, name, fileUrl }) => ({
                uid: id,
                name,
                status: "done",
                url: fileUrl,
              }),
            ) || [],
          contributionPaymentAgreement:
            map(
              filter(
                attachmentsList,
                ({ type }) =>
                  type === ATTACHMENT_TYPES.CONTRIBUTION_PAYMENT_AGREEMENT,
              ),
              ({ id, name, fileUrl }) => ({
                uid: id,
                name,
                status: "done",
                url: fileUrl,
              }),
            ) || [],
        });
      },
    },
  );

  // Determine payment conditions based on subscription data
  const paymentConditions = useMemo(() => {
    const subscriptionData = get(data, "subscription");
    const payment = get(subscriptionData, "payment");

    if (!payment) {
      return {
        isPayerReceiver: true,
        payerIsAdherent: true,
        receiverIsAdherent: true,
        requiresSubscriberPaysButDifferentReceiver: false, // Subscriber pays but doesn't receive reimbursements
        requiresThirdPartyPayer: false, // Third-party payer: Subscriber doesn't pay
        requiresBothThirdParty: false, // Subscriber doesn't pay AND doesn't receive
        requiresTwoSeparateRibs: false,
      };
    }

    // Check if payer and receiver are the same account
    const receiverId = get(payment, "receiver.id");
    const payerId = get(payment, "payer.id");
    const isPayerReceiver = receiverId === payerId;

    // Check if payer is the adherent (subscriber)
    const payerIsAdherent = get(payment, "payer.owner.isAdherent", false);

    // Check if receiver is the adherent (subscriber)
    const receiverIsAdherent = get(payment, "receiver.owner.isAdherent", false);

    // Subscriber pays premiums but doesn't receive reimbursements
    // Condition: payer is adherent AND receiver is NOT adherent AND payer ≠ receiver
    const requiresSubscriberPaysButDifferentReceiver =
      payerIsAdherent && !receiverIsAdherent && !isPayerReceiver;

    // Third-party payer: Subscriber doesn't pay premiums (someone else pays)
    // Condition: payer is NOT adherent
    const requiresThirdPartyPayer = !payerIsAdherent;

    // Subscriber doesn't pay AND doesn't receive
    // Condition: payer is NOT adherent AND receiver is NOT adherent AND payer ≠ receiver
    const requiresBothThirdParty =
      !payerIsAdherent && !receiverIsAdherent && !isPayerReceiver;

    // Requires 2 separate RIBs when payer and receiver are different accounts:
    // - Subscriber pays but different receiver (needs payer RIB + receiver RIB)
    // - Third-party payer with different receiver: needs payer RIB + receiver RIB
    // - Both third-party: needs payer RIB + receiver RIB
    const requiresTwoSeparateRibs = !isPayerReceiver;

    return {
      isPayerReceiver,
      payerIsAdherent,
      receiverIsAdherent,
      requiresSubscriberPaysButDifferentReceiver, // Requires: CIN adherent + CIN receiver + RIB receiver
      requiresThirdPartyPayer, // Requires: CIN adherent + CIN payer
      requiresBothThirdParty, // Requires: 3 CINs (adherent + payer + receiver) + 2 RIBs
      requiresTwoSeparateRibs, // Requires: 2 RIBs (payer RIB + receiver RIB)
    };
  }, [data]);

  const [addAttachment] = useMutation(CREATE_ATTACHMENT);
  const [deleteAttachment] = useMutation(DELETE_ATTACHMENT);
  const saveDocumentsCompletion = useSaveDocumentsCompletion(subscriptionId);
  const isSigned =
    get(data, "subscription.status") === SUBSCRIPTION_STATUSES.SIGNED;

  const handleFileRemove = (fieldName) => async (item) => {
    const fileId = item?.uid || item?.id;

    // If file is already uploaded (has an id and doesn't contain "AUTO"), delete it
    if (fileId && typeof fileId === "string" && !fileId.includes("AUTO")) {
      try {
        await deleteAttachment({
          variables: {
            where: { id: fileId },
          },
        });
        // Refetch attachments to update the UI
        if (refetchAttachments) {
          await refetchAttachments();
        }
      } catch (_error) {
        // Attachment deletion failures should not block field clear.
      }
    }

    // Return the field name so View.js can update the form values
    return fieldName;
  };

  const onSubmit = async (documents) => {
    setDocumentsLoading(true);
    const documentsToUpload = [];
    Object.entries(documents)?.forEach(([key, attachments]) => {
      if (Array.isArray(attachments) && attachments.length > 0) {
        attachments
          .filter((e) => !e.status)
          .forEach(({ name, contentType, base64 }) => {
            documentsToUpload.push({
              id: cuid(),
              subscription: { id: subscriptionId },
              name,
              content: base64,
              contentType,
              type: DOCUMENTS_TYPE[key],
              user: { id: get(data, "subscription.project.contact.user.id") },
            });
          });
      }
    });
    await Promise.all(
      documentsToUpload.map((document) =>
        addAttachment({
          variables: {
            data: document,
          },
        }),
      ),
    );

    const relatedPersons = get(project, "contact.relatedPersons", []).filter(
      (_) => _,
    );
    const hasChildren = !!relatedPersons.filter(
      ({ relationship }) => relationship === RELATIONSHIP.CHILD,
    ).length;
    const hasSpouse = !isEmpty(
      relatedPersons.find(
        ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
      ),
    );
    const isLocked = get(data, "subscription.locked");
    const hasRelatedPersons = isLocked
      ? { hasChildren, hasSpouse }
      : get(project, "fields.hasRelatedPersons");

    await saveDocumentsCompletion(
      isLastJustificationStep(JUSTIFICATION_STEPS.ADHERENT, hasRelatedPersons),
    );

    const source = localStorage.getItem(KEYS.SOURCE);
    updateUrl({
      subscriptionId,
      url: get(
        data,
        "subscription.insurancePlan.config.generatedUrl",
        `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
      ),
    });
    if (!isLocked)
      return generateSubscriptionDocuments({
        onCompleted: () => {
          return onNext({
            payload: { locked: true },
            onCompleted: () => setDocumentsLoading(false),
          });
        },
        onError: () => {
          setDocumentsLoading(false);
          notification.error({
            message:
              TRANSLATION?.ERRORS?.GENERATE_DOCUMENTS || API_ERRORS.GENERIC,
          });
        },
        variables: {
          data: { id: subscriptionId },
        },
      });
    return onNext({
      payload: {
        fields: {
          ...get(project, "fields"),
          hasRelatedPersons: {
            hasChildren,
            hasSpouse,
          },
        },
      },
      onCompleted: () => setDocumentsLoading(false),
    });
  };
  const downloadFile = async (id) => {
    await downloadSingleDocument({
      variables: { where: { id } },
      onCompleted: (_) => {
        const { document } = omitDeep(_.downloadSingleDocument, "__typename");
        downloadDocument(document);
      },
    });
  };
  if (attachmentLoading || isEmpty(initialValues) || isEmpty(data))
    return <Loader />;

  return (
    <>
      <Navbar
        project={project}
        currentStatus={currentStatus}
        tracer={tracer}
        showProfile={!isSigned}
        showCart={!isSigned}
        showHelp={!isSigned}
      />
      <View
        onSubmit={onSubmit}
        supportingDocuments={supportingDocuments}
        initialValues={initialValues}
        translate={TRANSLATION}
        downloadFile={downloadFile}
        isRia={get(data, "subscription.project.ria") === true}
        onBack={onBack}
        paymentConditions={paymentConditions}
        subscription={get(data, "subscription")}
        documentsLoading={documentsLoading}
        project={project}
        onFileRemove={handleFileRemove}
        isSigned={isSigned}
      />
    </>
  );
};
export default Justification;
